import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { navItems, languages, mainSiteUrl, siteName } from "@/mocks/site";
import { categories } from "@/mocks/categories";

export default function Header() {
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
      }
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest("[data-menu-toggle]")
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleNavClick = useCallback(
    (href: string, scrollTo?: string) => {
      setMenuOpen(false);
      if (scrollTo) {
        if (location.pathname === "/") {
          const el = document.getElementById(scrollTo);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        } else {
          navigate("/");
          // Wait for home page to mount then scroll
          const tryScroll = () => {
            const el = document.getElementById(scrollTo);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
              setTimeout(tryScroll, 100);
            }
          };
          setTimeout(tryScroll, 300);
        }
        return;
      }
      navigate(href);
    },
    [location.pathname, navigate],
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b-2 border-background-200 bg-white">
      <div className="w-full px-4 md:px-6">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-white">
              <i className="ri-file-list-3-line text-lg" aria-hidden="true"></i>
            </span>
            <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
              {siteName}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-0.5 lg:flex">
            {navItems.map((item) =>
              item.scrollTo ? (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleNavClick(item.href, item.scrollTo)}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 rounded-md border border-background-300 bg-white px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
              >
                <i className="ri-global-line text-sm" aria-hidden="true"></i>
                <span className="whitespace-nowrap">EN</span>
                <i className="ri-arrow-down-s-line text-xs" aria-hidden="true"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-white py-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => setLangOpen(false)}
                      className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-medium text-foreground-800 transition-colors hover:bg-primary-50"
                    >
                      <span>{lang.label}</span>
                      <span className="text-xs text-foreground-400">{lang.short}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-primary-600 sm:inline-flex"
            >
              Buy It Now
            </a>

            {/* Mobile menu toggle */}
            <button
              type="button"
              data-menu-toggle
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-900 md:hidden"
              aria-label="Toggle menu"
            >
              <i
                className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/30 md:hidden"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          ></div>

          {/* Mobile menu panel */}
          <div
            ref={menuRef}
            className="fixed inset-x-0 top-[66px] z-50 mx-2 max-h-[calc(100vh-80px)] overflow-y-auto rounded-b-xl border-2 border-t-0 border-background-200 bg-white shadow-lg md:hidden"
          >
            <nav className="flex flex-col gap-1 px-4 pt-4">
              {navItems.map((item) =>
                item.scrollTo ? (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => handleNavClick(item.href, item.scrollTo)}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-3 text-left text-sm font-semibold text-foreground-900 transition-colors hover:bg-primary-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-primary-700">
                      <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold text-foreground-900 transition-colors hover:bg-primary-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-primary-700">
                      <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    {item.label}
                  </Link>
                ),
              )}
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="mt-2 flex items-center justify-center gap-2 rounded-md bg-primary-500 px-3 py-3 text-sm font-bold text-white"
              >
                <i className="ri-shopping-bag-3-line text-base" aria-hidden="true"></i>
                Buy It Now
              </a>
            </nav>

            {/* Categories quick access */}
            <div className="mt-5 border-t border-background-200 px-4 pb-4 pt-4">
              <p className="px-3 text-xs font-bold uppercase tracking-widest text-foreground-500">
                Shop Categories
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
                  >
                    <span className="h-6 w-6 flex-shrink-0 overflow-hidden rounded">
                      <img
                        src={cat.image}
                        alt={`${cat.name} category`}
                        title={cat.name}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}